import React from "react";
import { MapPin, Building2, BookmarkPlus, Mail, ShieldCheck, FlaskConical } from "lucide-react";
import { Pill } from "@/components/common";
import { EnrichButton } from "@/components/EnrichButton";
import { useI18n } from "@/lib/i18n";
import { tDynamic } from "@/i18n/dynamic";

export function MatchBadge({ score, coverage }) {
  const { t } = useI18n();
  if (score == null) return <Pill testid="match-score">{t("No criteria")}</Pill>;
  const tone = score >= 75 ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" : score >= 50 ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground";
  return (
    <div className={`rounded-lg px-2.5 py-1 text-right ${tone}`} data-testid="match-score">
      <div className="text-lg font-bold leading-none font-mono">{score}%</div>
      <div className="text-[10px] font-medium opacity-80">{t("coverage {{pct}}%", { pct: Math.round((coverage || 0) * 100) })}</div>
    </div>
  );
}

export function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((x) => x[0]).join("").toUpperCase();
}

export function Avatar({ name, size = 10 }) {
  return <div style={{ width: size * 4, height: size * 4 }} className={`shrink-0 rounded-full bg-gradient-to-br from-slate-100 to-indigo-100 dark:from-slate-800 dark:to-indigo-950 flex items-center justify-center text-xs font-bold text-indigo-900 dark:text-indigo-200`}>{initials(name)}</div>;
}

export default function CandidateCard({ c, selected, onSelect, onSave, onContact, idx }) {
  const { t } = useI18n();
  const match = c.match;
  const top = match?.supported?.slice(0, 2) || [];
  return (
    <div onClick={() => onSelect(c)} data-testid={`candidate-card-${idx}`}
      className={`card-surface p-4 cursor-pointer transition-[border-color,box-shadow] duration-200 fade-up ${selected ? "border-primary ai-ring" : "hover:border-primary/40 hover:shadow-md"}`}
      style={{ animationDelay: `${Math.min(idx, 10) * 40}ms` }}>
      <div className="flex gap-3">
        <Avatar name={c.full_name} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-semibold truncate flex items-center gap-1.5" data-testid="candidate-name">
                {c.full_name}
                {c.verification_status === "verified_by_candidate" && <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" title={t("Verified by candidate")} />}
                {c.is_synthetic && <Pill tone="amber"><FlaskConical className="w-3 h-3" />{t("Synthetic test")}</Pill>}
              </div>
              <div className="text-[13px] text-muted-foreground truncate">{c.current_title || c.headline || t("Title unknown")}</div>
            </div>
            {match && <MatchBadge score={match.score} coverage={match.coverage} />}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-muted-foreground">
            {c.current_company && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{c.current_company}</span>}
            {(c.city || c.region || c.country_code) && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{[c.city, c.region, c.country_code].filter(Boolean).join(", ")}</span>}
            {c.years_experience != null && <span>{t("{{count}} yrs exp.", { count: c.years_experience })}</span>}
            <Pill>{c.scope === "private" ? t("Private pool") : t("Global catalog")}</Pill>
          </div>
          {c.skills?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2.5">
              {c.skills.slice(0, 6).map((s) => <span key={s.name} className="text-[11px] rounded-md border px-1.5 py-0.5 bg-background">{s.name}</span>)}
              {c.skills.length > 6 && <span className="text-[11px] text-muted-foreground">+{c.skills.length - 6}</span>}
            </div>
          )}
          {top.length > 0 && (
            <ul className="mt-2.5 space-y-0.5">
              {top.map((s, i) => <li key={i} className="text-xs text-muted-foreground"><span className="text-foreground font-medium">{tDynamic(s.criterion)}</span> — {tDynamic(s.evidence)}</li>)}
            </ul>
          )}
          <div className="flex gap-1.5 mt-3" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => onSave(c)} data-testid={`save-candidate-${idx}`} className="h-7 px-2.5 rounded-md border text-xs font-medium flex items-center gap-1 hover:bg-muted transition-colors"><BookmarkPlus className="w-3.5 h-3.5" />{t("Save")}</button>
            {c.contacts?.some((x) => x.type.includes("email")) && (
              <button onClick={() => onContact(c)} data-testid={`contact-candidate-${idx}`} className="h-7 px-2.5 rounded-md border text-xs font-medium flex items-center gap-1 hover:bg-muted transition-colors"><Mail className="w-3.5 h-3.5" />{t("Email available")}</button>
            )}
            {c.profile_url && <EnrichButton candidateId={c.id} enrichment={c.enrichment_email} kind="email" testid={`enrich-email-candidate-${idx}`} />}
            {c.profile_url && <EnrichButton candidateId={c.id} enrichment={c.enrichment_phone} kind="phone" testid={`enrich-phone-candidate-${idx}`} />}
          </div>
        </div>
      </div>
    </div>
  );
}
